/* eslint-disable indent */
import {
  ArrowLeftOutlined,
  CopyOutlined,
  DeleteOutlined,
  HistoryOutlined,
  PlusOutlined,
  ReloadOutlined,
  SendOutlined,
  UndoOutlined
} from "@ant-design/icons";
import {
  App,
  Button,
  Card,
  Divider,
  Input,
  Popconfirm,
  Progress,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";

import {
  ACTIVE_DISPATCH_STATES,
  DISPATCH_STATE_TAG_COLORS,
  ENTRY_STATUS,
  ENTRY_STATUS_TAG_COLORS,
  SKIP_REASON_LABELS,
  WAITLIST_API_ENDPOINTS,
  WAITLIST_TYPE_DESCRIPTIONS,
  WAITLIST_TYPE_LABELS,
  WAITLIST_TYPE_TAG_COLORS
} from "./constants";
import AddEntriesModal from "./EntriesModal/AddEntriesModal";
import DispatchProgressModal from "./EntriesModal/DispatchProgressModal";
import {
  buildWaitlistJoinUrl,
  computeDispatchProgressPercent,
  copyToClipboard,
  dispatchProgressStatus,
  isDispatchableType
} from "./helper";
import { showErrorNotification, showSuccessNotification } from "../../utils/notification";

const { Search } = Input;
const { Text, Title } = Typography;

const ENTRIES_PAGINATION_DEFAULTS = Object.freeze({ page: 1, limit: 20 });

// ─── WaitlistDetail ────────────────────────────────────────────────────────────

const WaitlistDetail = ({ waitlistConfig, ROOT_URL, onBack, onDispatchTriggered, onEdit }) => {
  const { modal } = App.useApp();
  const { token } = theme.useToken();

  const [entries, setEntries] = useState([]);
  const [isFetchingEntries, setIsFetchingEntries] = useState(false);
  const [deletingEntryId, setDeletingEntryId] = useState(null);
  const [isDispatching, setIsDispatching] = useState(false);
  const [isAddEntriesModalOpen, setIsAddEntriesModalOpen] = useState(false);
  const [isDispatchProgressModalOpen, setIsDispatchProgressModalOpen] = useState(false);
  const [requeuingEntryId, setRequeuingEntryId] = useState(null);
  const [isRequeuingAllSkipped, setIsRequeuingAllSkipped] = useState(false);

  const [emailSearchQuery, setEmailSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [skipReasonFilter, setSkipReasonFilter] = useState(undefined);
  const [currentPage, setCurrentPage] = useState(ENTRIES_PAGINATION_DEFAULTS.page);
  const [pageSize, setPageSize] = useState(ENTRIES_PAGINATION_DEFAULTS.limit);
  const [totalCount, setTotalCount] = useState(0);
  const [isJoinLinkCopied, setIsJoinLinkCopied] = useState(false);

  const configId = waitlistConfig?._id;
  const { entryCounts = {} } = waitlistConfig || {};
  const { total = 0, pending = 0, completed = 0, skipped = 0 } = entryCounts;
  const { latestDispatch } = waitlistConfig || {};

  // GENERAL_WAITLIST completes entries on join — no dispatch, no skip/pending concept.
  const supportsDispatch = isDispatchableType(waitlistConfig?.type);

  const hasActiveDispatch = ACTIVE_DISPATCH_STATES.includes(latestDispatch?.state);
  const isDispatchDisabled = hasActiveDispatch || pending === 0;
  const dispatchDisabledReason = hasActiveDispatch
    ? "A dispatch is already in progress"
    : "No pending entries to dispatch";

  const joinUrl = waitlistConfig?.uniquePublicId
    ? buildWaitlistJoinUrl(waitlistConfig.uniquePublicId)
    : "";

  const handleCopyJoinLink = () => {
    if (!joinUrl) {
      return;
    }
    copyToClipboard(joinUrl, () => {
      setIsJoinLinkCopied(true);
      setTimeout(() => setIsJoinLinkCopied(false), 1200);
    });
  };

  // ── Data fetching ────────────────────────────────────────────────────────────

  const fetchEntries = useCallback(() => {
    if (!configId) {
      return;
    }
    setIsFetchingEntries(true);
    axios
      .get(`${ROOT_URL}/${WAITLIST_API_ENDPOINTS.CONFIGS}/${configId}/entries`, {
        params: {
          page: currentPage,
          limit: pageSize,
          search: emailSearchQuery || undefined,
          status: statusFilter,
          skipReason: skipReasonFilter
        }
      })
      .then((res) => {
        const { entries: fetched, pagination } = res.data;
        setEntries(fetched);
        setTotalCount(pagination?.totalItems ?? 0);
      })
      .catch(showErrorNotification)
      .finally(() => setIsFetchingEntries(false));
  }, [ROOT_URL, configId, currentPage, pageSize, emailSearchQuery, statusFilter, skipReasonFilter]);

  const refreshEntriesAndCounts = useCallback(() => {
    fetchEntries();
    onDispatchTriggered();
  }, [fetchEntries, onDispatchTriggered]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleSearchChange = (value) => {
    setEmailSearchQuery(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleSkipReasonFilterChange = (value) => {
    setSkipReasonFilter(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleDeleteEntry = (entryId) => {
    setDeletingEntryId(entryId);
    axios
      .delete(`${ROOT_URL}/${WAITLIST_API_ENDPOINTS.CONFIGS}/${configId}/entries/${entryId}`)
      .then(() => {
        showSuccessNotification("Entry deleted.");
        fetchEntries();
        onDispatchTriggered();
      })
      .catch(showErrorNotification)
      .finally(() => setDeletingEntryId(null));
  };

  const requeueSkippedEntries = (entryIds, { onStart, onFinish }) => {
    onStart();
    axios
      .patch(`${ROOT_URL}/${WAITLIST_API_ENDPOINTS.CONFIGS}/${configId}/entries/requeue-skipped`, {
        entryIds
      })
      .then((res) => {
        const { requeuedCount = 0 } = res.data || {};
        showSuccessNotification(
          `Requeued ${requeuedCount} ${requeuedCount === 1 ? "entry" : "entries"}.`
        );
        fetchEntries();
        onDispatchTriggered();
      })
      .catch(showErrorNotification)
      .finally(onFinish);
  };

  const handleRequeueEntry = (entryId) =>
    requeueSkippedEntries([entryId], {
      onStart: () => setRequeuingEntryId(entryId),
      onFinish: () => setRequeuingEntryId(null)
    });

  const handleRequeueAllSkipped = () =>
    requeueSkippedEntries(undefined, {
      onStart: () => setIsRequeuingAllSkipped(true),
      onFinish: () => setIsRequeuingAllSkipped(false)
    });

  const triggerDispatch = () => {
    setIsDispatching(true);
    axios
      .post(`${ROOT_URL}/${WAITLIST_API_ENDPOINTS.CONFIGS}/${configId}/dispatches`)
      .then(() => {
        showSuccessNotification("Dispatch triggered!");
        setIsDispatchProgressModalOpen(true);
        onDispatchTriggered();
      })
      .catch(showErrorNotification)
      .finally(() => setIsDispatching(false));
  };

  const handleDispatchClick = () => {
    modal.confirm({
      title: "Dispatch waitlist",
      content: `Send to ${pending} pending ${pending === 1 ? "entry" : "entries"}?`,
      okText: "Dispatch",
      cancelText: "Cancel",
      onOk: triggerDispatch
    });
  };

  // ── Effects ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Refresh parent list counts on mount so dispatch button reflects reality, not a stale snapshot.
  useEffect(() => {
    onDispatchTriggered();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Entry table columns ──────────────────────────────────────────────────────

  const entryColumns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => (
        <Text strong style={{ fontSize: 13 }}>
          {email}
        </Text>
      )
    },
    // Status / skip-reason / completion only make sense for dispatchable types.
    ...(supportsDispatch
      ? [
          {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (status) => <Tag color={ENTRY_STATUS_TAG_COLORS[status]}>{status}</Tag>
          },
          {
            title: "Skip Reason",
            dataIndex: "skipReason",
            key: "skipReason",
            width: 170,
            render: (skipReason, entry) =>
              entry.status === ENTRY_STATUS.SKIPPED && skipReason ? (
                <Tag color="volcano">{SKIP_REASON_LABELS[skipReason] || skipReason}</Tag>
              ) : (
                <Text type="secondary">--</Text>
              )
          }
        ]
      : []),
    {
      title: "Joined At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 170,
      render: (v) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {v ? dayjs(v).format("DD MMM YYYY | HH:mm") : "--"}
        </Text>
      )
    },
    ...(supportsDispatch
      ? [
          {
            title: "Completed At",
            dataIndex: "completedAt",
            key: "completedAt",
            width: 170,
            render: (v) => (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {v ? dayjs(v).format("DD MMM YYYY | HH:mm") : "--"}
              </Text>
            )
          }
        ]
      : []),
    {
      title: "Actions",
      key: "actions",
      width: 90,
      render: (_, entry) => (
        <Space size={4}>
          {supportsDispatch && entry.status === ENTRY_STATUS.SKIPPED && (
            <Popconfirm
              title="Requeue Entry"
              description="Move back to Pending so the next dispatch retries it?"
              onConfirm={() => handleRequeueEntry(entry._id)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Requeue — move back to Pending">
                <Button
                  type="text"
                  icon={<UndoOutlined />}
                  loading={requeuingEntryId === entry._id}
                />
              </Tooltip>
            </Popconfirm>
          )}
          <Popconfirm
            title="Delete Entry"
            description="Are you sure you want to delete this entry?"
            onConfirm={() => handleDeleteEntry(entry._id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              loading={deletingEntryId === entry._id}
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  // ── Stats ────────────────────────────────────────────────────────────────────

  const statItems = supportsDispatch
    ? [
        { label: "Total", value: total },
        { label: "Pending", value: pending, valueStyle: pending > 0 ? { color: "#D4A017" } : {} },
        {
          label: "Completed",
          value: completed,
          valueStyle: completed > 0 ? { color: "#52C41A" } : {}
        },
        { label: "Skipped", value: skipped, valueStyle: skipped > 0 ? { color: "#8C8C8C" } : {} }
      ]
    : // GENERAL_WAITLIST: entries complete on join, so total === members; no pending/skipped.
      [{ label: "Members", value: total, valueStyle: total > 0 ? { color: "#52C41A" } : {} }];

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: "16px 32px" }}>
      {/* ── Unified header ── */}
      <div style={{ marginBottom: 16 }}>
        {/* Nav row */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10
          }}
        >
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
            style={{ paddingLeft: 0 }}
          >
            All Waitlists
          </Button>
          <Space>
            <Tooltip title={isJoinLinkCopied ? "Copied!" : joinUrl}>
              <Button icon={<CopyOutlined />} onClick={handleCopyJoinLink}>
                {isJoinLinkCopied ? "Copied!" : "Copy Join Link"}
              </Button>
            </Tooltip>
            {supportsDispatch && (
              <Button
                icon={<HistoryOutlined />}
                onClick={() => setIsDispatchProgressModalOpen(true)}
              >
                Dispatch History
              </Button>
            )}
            <Button onClick={onEdit}>Edit Config</Button>
          </Space>
        </div>

        {/* Title + stats in one horizontal row */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 24,
            flexWrap: "wrap"
          }}
        >
          {/* Left: name, tags, description, meta */}
          <div style={{ flex: "1 1 280px", minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
                marginBottom: 4
              }}
            >
              <Title level={4} style={{ margin: 0 }}>
                {waitlistConfig?.name}
              </Title>
              <Tag color={WAITLIST_TYPE_TAG_COLORS[waitlistConfig?.type]} style={{ margin: 0 }}>
                {WAITLIST_TYPE_LABELS[waitlistConfig?.type]}
              </Tag>
              <Tag color={waitlistConfig?.isActive ? "green" : "default"} style={{ margin: 0 }}>
                {waitlistConfig?.isActive ? "Active" : "Inactive"}
              </Tag>
            </div>
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
              {WAITLIST_TYPE_DESCRIPTIONS[waitlistConfig?.type]}
            </Text>
            {waitlistConfig?.description && (
              <Text style={{ fontSize: 13, display: "block", marginBottom: 6 }}>
                {waitlistConfig.description}
              </Text>
            )}
            <Space size={12} wrap>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <Text code style={{ fontSize: 11 }}>
                  {waitlistConfig?.uniquePublicId}
                </Text>
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Created:{" "}
                {waitlistConfig?.createdAt
                  ? dayjs(waitlistConfig.createdAt).format("DD MMM YYYY")
                  : "--"}
              </Text>
              {latestDispatch && (
                <Space size={6} align="center">
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Last dispatch:
                  </Text>
                  <Tag
                    color={DISPATCH_STATE_TAG_COLORS[latestDispatch.state]}
                    style={{ margin: 0 }}
                  >
                    {latestDispatch.state}
                  </Tag>
                </Space>
              )}
            </Space>
            {hasActiveDispatch && (
              <Progress
                percent={computeDispatchProgressPercent(latestDispatch)}
                status={dispatchProgressStatus(latestDispatch?.state)}
                size="small"
                style={{ marginTop: 8, maxWidth: 400 }}
              />
            )}
          </div>

          {/* Right: stat strip — boxed, themed, no wrapping */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              background: token.colorFillAlter,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadiusLG,
              padding: "10px 4px"
            }}
          >
            {statItems.map(({ label, value, valueStyle = {} }, i) => (
              <div key={label} style={{ display: "flex", alignItems: "center" }}>
                <div style={{ textAlign: "center", padding: "0 20px", minWidth: 90 }}>
                  <Statistic
                    title={<span style={{ fontSize: 12 }}>{label}</span>}
                    value={value}
                    valueStyle={{ fontSize: 20, fontWeight: 600, ...valueStyle }}
                  />
                </div>
                {i < statItems.length - 1 && (
                  <Divider type="vertical" style={{ height: 32, margin: 0 }} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <Divider style={{ margin: "0 0 16px" }} />

      {/* ── Entries table section ── */}
      <Card
        title={<Text strong>Entries</Text>}
        extra={
          <Space wrap>
            {supportsDispatch && (
              <Tooltip title={isDispatchDisabled ? dispatchDisabledReason : ""}>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  disabled={isDispatchDisabled}
                  loading={isDispatching}
                  onClick={handleDispatchClick}
                >
                  Dispatch ({pending} pending)
                </Button>
              </Tooltip>
            )}
            {supportsDispatch && (
              <Popconfirm
                title="Requeue all skipped"
                description="Move every skipped entry back to Pending?"
                onConfirm={handleRequeueAllSkipped}
                okText="Yes"
                cancelText="No"
                disabled={skipped === 0}
              >
                <Tooltip
                  title={skipped === 0 ? "No skipped entries" : "Requeue all skipped entries"}
                >
                  <Button
                    icon={<UndoOutlined />}
                    loading={isRequeuingAllSkipped}
                    disabled={skipped === 0}
                  >
                    Requeue Skipped ({skipped})
                  </Button>
                </Tooltip>
              </Popconfirm>
            )}
            <Button icon={<PlusOutlined />} onClick={() => setIsAddEntriesModalOpen(true)}>
              Add Entries
            </Button>
            <Tooltip title="Refresh entries & counts">
              <Button
                icon={<ReloadOutlined />}
                onClick={refreshEntriesAndCounts}
                loading={isFetchingEntries}
              />
            </Tooltip>
          </Space>
        }
      >
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
          <Search
            placeholder="Search by email"
            allowClear
            onSearch={handleSearchChange}
            style={{ width: 280 }}
          />
          {supportsDispatch && (
            <Select
              placeholder="Filter by status"
              allowClear
              style={{ width: 180 }}
              value={statusFilter}
              onChange={handleStatusFilterChange}
              options={Object.values(ENTRY_STATUS).map((s) => ({ label: s, value: s }))}
            />
          )}
          {supportsDispatch && (
            <Select
              placeholder="Filter by skip reason"
              allowClear
              style={{ width: 200 }}
              value={skipReasonFilter}
              onChange={handleSkipReasonFilterChange}
              options={Object.entries(SKIP_REASON_LABELS).map(([value, label]) => ({
                label,
                value
              }))}
            />
          )}
        </div>

        <Table
          columns={entryColumns}
          dataSource={entries}
          rowKey="_id"
          loading={isFetchingEntries}
          bordered
          size="small"
          pagination={{
            current: currentPage,
            pageSize,
            total: totalCount,
            showSizeChanger: true,
            showTotal: (t, r) => `${r[0]}-${r[1]} of ${t} entries`,
            pageSizeOptions: ["10", "20", "50"],
            onChange: handlePageChange,
            onShowSizeChange: handlePageChange
          }}
        />
      </Card>

      {isAddEntriesModalOpen && (
        <AddEntriesModal
          configId={configId}
          ROOT_URL={ROOT_URL}
          isModalOpen={isAddEntriesModalOpen}
          setIsModalOpen={setIsAddEntriesModalOpen}
          onSuccess={() => {
            fetchEntries();
            onDispatchTriggered();
          }}
        />
      )}
      {isDispatchProgressModalOpen && (
        <DispatchProgressModal
          configId={configId}
          waitlistConfigName={waitlistConfig?.name}
          ROOT_URL={ROOT_URL}
          isModalOpen={isDispatchProgressModalOpen}
          setIsModalOpen={(open) => {
            setIsDispatchProgressModalOpen(open);
            // Closing the progress view refreshes counts so the dispatch button reflects the finished run.
            if (!open) refreshEntriesAndCounts();
          }}
        />
      )}
    </div>
  );
};

export { WaitlistDetail };
